import {IHierarchyNodeProvider, INode, INodeLogicalKey} from "../PA.MD.Core/PA.MD.IF.Hierarchy";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_Customer,
    esWB_Customer_Attribute,
    esWB_Customer_Distribution_Relationship,
    esWB_Customer_LogicalKey,
    esWB_Customer_Relationship,
    esWB_Customer_Role,
    esWB_Customer_Role_Customer_Relationship, esWB_Customer_Role_Relationship,
    esWB_Customer_SalesArea_Relationship,
    esWB_Customer_Tag_Relationship,
    esWB_Customer_Type,
    esWB_Entity, esWB_MasterData_Set
} from "../PA.Core/PAdbSchema";
import * as _ from "lodash"
import {ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {WB_CustomerLogicalKeyRow, WB_CustomerRow, WB_EntityRow, WB_MasterDataSetRow} from "../PA.Core/PAdbSchemaRows";
import {CustomerNode} from "./CustomerNode";
import {HierarchyUtils} from "../PA.Core/Basic/HierarchyUtils";
import * as AsyncLock from 'async-lock';
import {TextUtils} from "../PA.Core/Basic/Text";
import {HierarchyService} from "./HierarchyService";


const knownMappers = {
    [esWB_Customer]: esWB_Customer
}

export class CustomerNodeProvider
    implements IHierarchyNodeProvider {
    private lkNodeLock:AsyncLock

    private dbContext: PAdbContext
    private wbMDSetId: number
    private masterDataSetRow: WB_MasterDataSetRow
    private dctByWbId: { [mapperId: string]: { [wbId: number]: INode } }
    private dctByKeySetId: { [mapperId: string]: { [keySetId: string]: { [Id: string]: INode } } }
    private dctByLKHash: { [hashKey: string]: INode }

    private loaded: boolean
    private arrEntityTypes: WB_EntityRow[]

    constructor(dbContext: PAdbContext, wbMDSetId: number) {
        this.dbContext = dbContext
        this.wbMDSetId = wbMDSetId
        this.dctByWbId = {[esWB_Customer]: {}}
        this.dctByKeySetId = {[esWB_Customer]: {}}
        this.dctByLKHash = {}
        this.masterDataSetRow = undefined
        this.loaded = false
        this.lkNodeLock = new AsyncLock({})
        this.arrEntityTypes = []
    }

    get bLoaded(): boolean { return this.loaded }

    async OnLoadComplete(): Promise<void> {
        await this.checkLoaded()
    }

    private async checkLoaded(){
        if(this.loaded)
            return

        this.masterDataSetRow = <WB_MasterDataSetRow> await this.dbContext.find(esWB_MasterData_Set, this.wbMDSetId)

        this.arrEntityTypes = <WB_EntityRow[]> await this.dbContext.findAll(esWB_Entity, {
            where: {
                WB_MDSET_ID: this.wbMDSetId,
                ENTITY_DIMENSION: "Customer",
                DELETED_IND: {neq: '1'}
            }
        })

        this.loaded = true

    }

    // Async, read from Source as needed, loads into cache
    //-- Find one
    async findNodeByWbId(mapperName: string, wbId: number): Promise<INode> {
        await this.checkLoaded()

        let arr = await this.findNodesByWbIds(mapperName, [wbId])
        if (arr.length)
            return arr[0]

        return undefined
    }


    async findNodeByKeySetId(mapperName: string, sKeySetId: string, sId: string): Promise<INode> {
        await this.checkLoaded()

        let arr = await this.findNodesByKeySetIds(mapperName, sKeySetId, [sId])
        if (arr.length)
            return arr[0]

        return undefined
    }

    //-- Find multiple
    async findNodesByWbIds(mapperName: string, wbIds: number[]): Promise<INode[]> {
        await this.checkLoaded()

        if (!(mapperName in knownMappers))
            return []

        let searchIds: number[] = []
        let rv: INode[] = []
        _.forEach(wbIds, (wbId) => {
            let nd = this.findNodeByWbIdSync(mapperName, wbId)
            if (_.isEmpty(nd))
                searchIds.push(wbId)
            else
                rv.push(nd)
        })

        if (_.isEmpty(searchIds))
            return rv

        let rr = this.getRecordResolver()
        let rows: WB_CustomerRow[] = <WB_CustomerRow[]>await rr.findAllWithRelationships(mapperName, searchIds)
        for (let aRow of rows) {
            let cn = new CustomerNode(aRow, this)
            this.addNodeToCache(cn)
            rv.push(cn)
        }

        return rv
    }

    async findNodesByKeySetIds(mapperName: string, sKeySetId: string, arrIds: string[]): Promise<INode[]> {
        await this.checkLoaded()

        if (!(mapperName in knownMappers))
            return []

        let searchIds: string[] = []
        let rv: INode[] = []
        _.forEach(arrIds, (sId) => {
            let nd = this.findNodeByKeySetIdSync(mapperName, sKeySetId, sId)
            if (_.isEmpty(nd))
                searchIds.push(sId)
            else
                rv.push(nd)
        })

        if (_.isEmpty(searchIds))
            return rv

        let rr = this.getRecordResolver()
        let queryCnd = {
            WB_MDSET_ID: this.wbMDSetId,
            CUSTOMER_SET_ID: sKeySetId, CUSTOMER_ID: {inq: searchIds},
            DELETED_IND: {neq: '1'}
        }
        let rows: WB_CustomerRow[] = <WB_CustomerRow[]>await rr.queryAllWithRelationships(mapperName, {
            where: queryCnd
        })
        for (let aRow of rows) {
            let cn = new CustomerNode(aRow, this)
            this.addNodeToCache(cn)
            rv.push(cn)
        }

        return rv

    }

    // Sync, pull from cache only
    findNodeByWbIdSync(mapperName: string, wbId: number): INode {
        if (!(mapperName in knownMappers))
            return undefined

        let dct = this.dctByWbId[mapperName] || {}
        if (wbId in dct)
            return dct[wbId]

        return undefined
    }

    findNodeByKeySetIdSync(mapperName: string, keySetId: string, id: string): INode {
        if (!(mapperName in knownMappers))
            return undefined

        let dctL1 = this.dctByKeySetId[mapperName] || {}
        if (!(keySetId in dctL1))
            return undefined

        let dctL2 = dctL1[keySetId]
        if (!(id in dctL2))
            return undefined

        return dctL2[id]
    }

    findNodeByLogicalKeySync(logicalKey: INodeLogicalKey): INode {

        let sLKHash = HierarchyUtils.getLogicKeyHash(logicalKey)

        if(_.isEmpty(sLKHash))
            return undefined
        if(sLKHash in this.dctByLKHash)
            return this.dctByLKHash[sLKHash]

        return undefined
    }

    async findNodeByLogicalKey(logicalKey: INodeLogicalKey): Promise<INode> {
        await this.checkLoaded()

        let sLKHash = HierarchyUtils.getLogicKeyHash(logicalKey)
        if(_.isEmpty(sLKHash))
            return undefined

        return await this.lkNodeLock.acquire(sLKHash, (done) => {

            // A. Check in Cache
            if(sLKHash in this.dctByLKHash) {
                done(undefined, this.dctByLKHash[sLKHash])
                return

            }

            (async () => {

                // B. Check in DB
                let hs = new HierarchyService()
                let info = await hs.getNodeWbIdByLKNode(this.wbMDSetId, "Customer", logicalKey)

                let wbId_found = info.wbIds[0] || 0
                if(info.wbIds?.length == 1){
                    if(wbId_found > 0){
                        let nd = await this.findNodeByWbId(esWB_Customer, wbId_found)
                        done(undefined, nd)
                        return
                    }
                } else if(info.dupError){
                    done(new Error(`Duplicate Hierarchy Node Err wbIds: ${info.wbIds.join(", ")}`), undefined)
                    return
                }
                done(undefined, undefined)

            })();
        })
    }

    // async for MD Generated Hierarchy
    // A. Check in Cache
    // B. Check in DB
    // C. Create if not found
    async findOrCreateLogicalKeyNode(logicalKey: INodeLogicalKey, options?: {sCreateId?: string, sCreateName?: string, dbContext?: PAdbContext}): Promise<INode> {
        await this.checkLoaded()

        let sLKHash = HierarchyUtils.getLogicKeyHash(logicalKey)
        if(_.isEmpty(sLKHash))
            return undefined

        let opts = options || {}

        return await this.lkNodeLock.acquire(sLKHash, (done) => {

            // A. Check in Cache
            if(sLKHash in this.dctByLKHash) {
                done(undefined, this.dctByLKHash[sLKHash])
                return

            }

            (async () => {

                // B. Check in DB
                let hs = new HierarchyService()
                let info = await hs.getNodeWbIdByLKNode(this.wbMDSetId, "Customer", logicalKey)

                let wbId_found = info.wbIds[0] || 0
                if(info.wbIds?.length == 1){
                    if(wbId_found > 0){
                        let nd = await this.findNodeByWbId(esWB_Customer, wbId_found)
                        done(undefined, nd)
                        return
                    }
                } else if(info.dupError){
                    done(new Error(`Duplicate Hierarchy Node Err wbIds: ${info.wbIds.join(", ")}`), undefined)
                    return
                }

                // C. Create Node if not found
                let setId = 'HNode'
                let aHR = <WB_CustomerRow>await this.dbContext.createRecordWithKey(esWB_Customer)
                let wbId = aHR.cif.WB_CUST_ID
                let id_ui = TextUtils.padStart(wbId.toString(36), 12, '0').toUpperCase()
                aHR.cif.CUSTOMER_ID = opts.sCreateId ||  ('HN-' + id_ui)
                aHR.cif.CUSTOMER_NAME = opts.sCreateName || id_ui
                aHR.cif.WB_MDSET_ID = this.wbMDSetId
                aHR.cif.CUSTOMER_SET_ID = setId
                aHR.cif.HIER_NODE_IND = '1'
                this.dbContext.add(esWB_Customer, aHR)
                for (let aKey in logicalKey) {
                    let eLK = logicalKey[aKey]
                    let lkComp = <WB_CustomerLogicalKeyRow>await this.dbContext.createRecordWithKey(esWB_Customer_LogicalKey)
                    lkComp.cif.WB_CUST_ID = wbId
                    lkComp.WB_CustomerRow = aHR
                    lkComp.cif.KEY_ID = aKey
                    lkComp.cif.DATATYPE = eLK.dataType
                    lkComp.cif.VALUE_NUMBER = eLK.valueNumber || 0
                    lkComp.cif.VALUE_STRING = eLK.valueString || ""
                    lkComp.cif.VALUE_WB_ENTITY_ID = eLK.valueWbEntityId || 0
                    lkComp.cif.VALUE_WB_ID = eLK.valueWbId || 0
                    lkComp.cif.VALUE_WB_ATTRIBUTEM_ID = eLK.valueWbAttributeMId || 0
                    this.dbContext.add(esWB_Customer_LogicalKey, lkComp)
                }

                // create INode
                let cn = new CustomerNode(aHR, this)

                // Saving Node
                let dbContext = opts.dbContext || this.dbContext
                dbContext.BatchStart()
                await dbContext.SaveBatch(esWB_Customer, aHR)
                await dbContext.SaveBatch(esWB_Customer_LogicalKey, aHR.WB_CustomerLogicalKeyRows)
                await dbContext.BatchComplete()

                this.addNodeToCache(cn)

                done(undefined, cn)

            })();


        })

    }

    get entityTypes(): WB_EntityRow[] {
        return this.arrEntityTypes
    }

    // Private Functions
    private addNodeToCache(aNode: INode): void {
        if (_.isEmpty(aNode))
            return

        let mapperId = aNode.mapperName
        let wbId = aNode.wbId
        let keySetId = aNode.key_set
        let id_ui = aNode.id_ui

        // Always add to wbId dictionary
        if (!(mapperId in this.dctByWbId))
            this.dctByWbId[mapperId] = {}
        if (!(wbId in this.dctByWbId[mapperId]))
            this.dctByWbId[mapperId][wbId] = aNode

        if (aNode.bUseLogicalKey) {
            let sLKHash = HierarchyUtils.getLogicKeyHash(aNode.logicalKey)
            this.dctByLKHash[sLKHash] = aNode
        }

        if (!(mapperId in this.dctByKeySetId))
            this.dctByKeySetId[mapperId] = {}
        if (!(keySetId in this.dctByKeySetId[mapperId]))
            this.dctByKeySetId[mapperId][keySetId] = {}
        if (!(id_ui in this.dctByKeySetId[mapperId][keySetId]))
            this.dctByKeySetId[mapperId][keySetId][id_ui] = aNode

    }





    private getRecordResolver(): ESRecordResolver {
        let rr = new ESRecordResolver(this.dbContext, {
            standardConditions: {
                [esWB_Customer]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Customer_LogicalKey]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Customer_Role_Relationship]: {"DELETED_IND": {"notIn": ['1']}},
            },
            dctExcludeHasManyNamedRelations: {
                // We only want: esWB_Customer_LogicalKey, Roles
                [esWB_Customer]: [
                    esWB_Customer_Relationship,
                    esWB_Customer_Attribute,
                    esWB_Customer_Distribution_Relationship,
                    esWB_Customer_Tag_Relationship,
                    esWB_Customer_SalesArea_Relationship,
                    esWB_Customer_Role_Customer_Relationship
                ]
            },
            dctIncludeBelongsToNamedRelations: {
                [esWB_Customer_Role]: [esWB_Customer_Type]
            }
        })

        return rr
    }


}